/**
 * BOM Extractor API 2 Page
 * 
 * Integrated PDF extraction tool with:
 * - Dynamic format dropdown fetched from extractor
 * - AI chatbot for format tuning (inline right column, compact)
 * - Embedded Format Builder (collapsible, restricted)
 * - Image paste/attach in chatbot
 * - Help guide modal
 * 
 * Access restricted to BOM_ACCESS_EMAILS on the backend.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import FormatBuilderPanel from './FormatBuilder/FormatBuilderPanel';
import RegionEditor from './FormatBuilder/RegionEditor';
import { groupFormatsByFirm, normalizeRegionsForEditor } from './FormatBuilder/formatBuilderUtils';
import { ListenButton } from '../common/AudioGuide';
import HelpVideoTour from '../Help/HelpVideoTour';
import { hasHelpTour } from '../Help/HelpWithVideoTour';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';

const BomExtractorApi2Page = ({ jobNumber }) => {
  const { instance, accounts } = useMsal();
  const { viewers, isConnected, connectionState } = usePresence('bomExtractorApi2', 'global', true);

  const [selectedFile, setSelectedFile] = useState(null);
  const [extractionOptions, setExtractionOptions] = useState({
    unitSystem: 'Imperial', format: '', drawingFormatId: null, fileType: 'Isometrics',
  });
  const [isUploading, setIsUploading] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [error, setError] = useState(null);
  const [serviceHealth, setServiceHealth] = useState(null);

  const [availableFormats, setAvailableFormats] = useState([]);
  const [formatsLoading, setFormatsLoading] = useState(true);
  const [formatsError, setFormatsError] = useState(null);

  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [lastExtractionResults, setLastExtractionResults] = useState(null);
  const [chatImage, setChatImage] = useState(null);

  const [formatBuilderAccess, setFormatBuilderAccess] = useState(false);
  const [formatBuilderOpen, setFormatBuilderOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [restartMessage, setRestartMessage] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const [extractorMode, setExtractorMode] = useState('v2');
  const [v2Formats, setV2Formats] = useState([]);
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [v2JobHistory, setV2JobHistory] = useState([]);
  const [stallWarning, setStallWarning] = useState(false);
  const [regionEditorAccess, setRegionEditorAccess] = useState(false);
  const [regionEditorOpen, setRegionEditorOpen] = useState(false);
  const [regionEditorRegions, setRegionEditorRegions] = useState([]);
  const [v2BuilderAccess, setV2BuilderAccess] = useState(false);

  const fileInputRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const lastProgressRef = useRef({ value: -1, time: 0 });
  const chatEndRef = useRef(null);
  const chatImageInputRef = useRef(null);

  const fetchFormats = useCallback(async () => {
    setFormatsLoading(true);
    setFormatsError(null);
    try {
      const [legacyRes, v2Res] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', '/pdf-extractor/formats').catch(() => null),
        authenticatedApiCall(instance, accounts, 'GET', '/pdf-extractor/v2/formats').catch(() => null),
      ]);
      if (legacyRes?.data?.success && legacyRes.data.data?.formats) {
        setAvailableFormats(legacyRes.data.data.formats);
      }
      if (v2Res?.data?.success && v2Res.data.data?.formats) {
        setV2Formats(v2Res.data.data.formats);
        if (v2Res.data.data.formats.length > 0 && !extractionOptions.drawingFormatId) {
          const first = v2Res.data.data.formats[0];
          setExtractionOptions(prev => ({ ...prev, format: first.slug, drawingFormatId: first.id }));
        }
      }
    } catch (err) {
      setFormatsError('Could not load formats');
    } finally {
      setFormatsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, accounts]);

  const checkHealth = useCallback(async () => {
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', '/pdf-extractor/health');
      setServiceHealth(response.data.success ? 'online' : 'offline');
    } catch { setServiceHealth('offline'); }
  }, [instance, accounts]);

  const checkAccess = useCallback(async () => {
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', '/pdf-extractor/format-builder-access');
      if (response.data.success) setFormatBuilderAccess(response.data.hasAccess);
    } catch { setFormatBuilderAccess(false); }
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', '/pdf-extractor/v2/region-editor-access');
      if (response.data.success) setRegionEditorAccess(response.data.hasAccess);
    } catch { setRegionEditorAccess(false); }
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', '/pdf-extractor/v2/format-builder-access');
      if (response.data.success) setV2BuilderAccess(response.data.hasAccess);
    } catch { setV2BuilderAccess(false); }
  }, [instance, accounts]);

  const handleRestartService = useCallback(async () => {
    setRestarting(true);
    setRestartMessage(null);
    try {
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/pdf-extractor/restart-service');
      if (response.data.success) {
        setRestartMessage({ type: 'success', text: response.data.message });
        setTimeout(() => { checkHealth(); fetchFormats(); }, 3000);
      } else {
        setRestartMessage({ type: 'error', text: response.data.error || 'Restart failed' });
      }
    } catch (err) {
      setRestartMessage({ type: 'error', text: 'Could not restart service: ' + (err.response?.data?.error || err.message) });
    } finally {
      setRestarting(false);
    }
  }, [instance, accounts, checkHealth, fetchFormats]);

  useEffect(() => { checkHealth(); fetchFormats(); checkAccess(); }, [checkHealth, fetchFormats, checkAccess]);
  useEffect(() => { return () => { if (pollIntervalRef.current) clearInterval(pollIntervalRef.current); }; }, []);
  useEffect(() => { if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') { setSelectedFile(file); setError(null); }
    else { setError('Please select a PDF file'); setSelectedFile(null); }
  };

  const handleOptionChange = (key, value) => {
    if (key === 'format') {
      const sel = availableFormats.find(f => f.slug === value);
      setExtractionOptions(prev => ({ ...prev, format: value, drawingFormatId: sel?.id || null }));
    } else setExtractionOptions(prev => ({ ...prev, [key]: value }));
  };

  const pollJobStatus = useCallback(async (jobId, mode) => {
    try {
      const endpoint = mode === 'v2' ? `/pdf-extractor/v2/status/${jobId}` : `/pdf-extractor/status/${jobId}`;
      const response = await authenticatedApiCall(instance, accounts, 'GET', endpoint);
      if (response.data.success) {
        const data = response.data.data;
        setJobStatus(data);

        const currentProgress = data.progress || 0;
        const now = Date.now();
        if (currentProgress !== lastProgressRef.current.value) {
          lastProgressRef.current = { value: currentProgress, time: now };
          setStallWarning(false);
        } else if (now - lastProgressRef.current.time > 5 * 60 * 1000) {
          setStallWarning(true);
        }

        const doneStatuses = mode === 'v2' ? ['success', 'failed'] : ['success', 'error'];
        if (doneStatuses.includes(data.status) && pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current); pollIntervalRef.current = null;
          setStallWarning(false);
          if (data.status === 'success') {
            setLastExtractionResults({
              jobId,
              format: data.format_name,
              totalPages: data.total_pages,
              processedPages: data.processed_pages,
              stats: data.stats,
              error: null,
            });
          } else if (data.status === 'failed' || data.status === 'error') {
            setLastExtractionResults({
              jobId,
              error: data.error_message || data.error || 'Extraction failed',
            });
          }
        }
      }
    } catch {}
  }, [instance, accounts]);

  const handleExtract = async () => {
    if (!selectedFile) { setError('Please select a PDF file first'); return; }
    setIsUploading(true); setError(null); setJobStatus(null); setStallWarning(false);
    lastProgressRef.current = { value: -1, time: Date.now() };
    const mode = extractorMode;
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (mode === 'v2') {
        formData.append('formatId', extractionOptions.drawingFormatId);
        formData.append('drawingFormatId', extractionOptions.drawingFormatId);
        formData.append('notifyEmail', notifyEmail ? 'true' : 'false');
      } else {
        formData.append('unitSystem', extractionOptions.unitSystem);
        formData.append('format', extractionOptions.format);
        formData.append('fileType', extractionOptions.fileType);
        if (extractionOptions.drawingFormatId) formData.append('drawingFormatId', extractionOptions.drawingFormatId);
      }
      const endpoint = mode === 'v2' ? '/pdf-extractor/v2/extract' : '/pdf-extractor/extract';
      const response = await authenticatedApiCall(instance, accounts, 'POST', endpoint, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (response.data.success) {
        const jobId = response.data.data.jobId;
        setCurrentJob({ id: jobId, filename: selectedFile.name, startedAt: new Date().toISOString(), mode });
        setJobStatus({ status: mode === 'v2' ? 'pending' : 'processing', progress: 0 });
        pollIntervalRef.current = setInterval(() => pollJobStatus(jobId, mode), 3000);
        pollJobStatus(jobId, mode);
      } else setError(response.data.error || 'Failed to start extraction');
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.error || err.message;
      const details = err.response?.data?.details;
      const code = err.response?.data?.code;
      const sizeMB = selectedFile ? (selectedFile.size / 1024 / 1024).toFixed(1) : '?';
      const parts = [msg];
      if (details) parts.push(details);
      if (status) parts.push(`(HTTP ${status})`);
      if (code) parts.push(`[${code}]`);
      parts.push(`File: ${selectedFile?.name} (${sizeMB}MB)`);
      setError(parts.join(' — '));
    }
    finally { setIsUploading(false); }
  };

  const handleDownload = async () => {
    if (!currentJob?.id) return;
    setError(null);
    setDownloading(true);
    try {
      const mode = currentJob.mode || extractorMode;
      let endpoint;
      if (mode === 'v2') {
        endpoint = `/pdf-extractor/v2/download/${currentJob.id}`;
      } else {
        const fmtParam = extractionOptions.drawingFormatId ? `?formatId=${extractionOptions.drawingFormatId}` : '';
        endpoint = `/pdf-extractor/download/${currentJob.id}${fmtParam}`;
      }
      const response = await authenticatedApiCall(instance, accounts, 'GET', endpoint, null, { responseType: 'blob', timeout: 120000 });

      const contentType = response.headers?.['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await response.data.text();
        const json = JSON.parse(text);
        setError(json.error || json.details || 'Server returned an error');
        return;
      }

      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = `${currentJob.filename.replace('.pdf', '')}_extracted.xlsx`;
      document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); document.body.removeChild(a);
    } catch (err) {
      console.error('[BOM Download] Failed:', err);
      let msg = 'Failed to download result file';
      if (err.response?.data) {
        try {
          const text = err.response.data instanceof Blob
            ? await err.response.data.text()
            : JSON.stringify(err.response.data);
          const parsed = typeof text === 'string' ? JSON.parse(text) : text;
          msg = parsed.error || parsed.details || msg;
        } catch { /* blob parse failed, use generic msg */ }
      } else if (err.message) {
        msg += `: ${err.message}`;
      }
      setError(msg);
    } finally {
      setDownloading(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null); setCurrentJob(null); setJobStatus(null); setError(null); setLastExtractionResults(null); setStallWarning(false);
    lastProgressRef.current = { value: -1, time: 0 };
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
  };

  // ── Chat with image support ───────────────────────────

  const handleImagePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        const reader = new FileReader();
        reader.onload = (ev) => setChatImage(ev.target.result);
        reader.readAsDataURL(file);
        return;
      }
    }
  };

  const handleImageAttach = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setChatImage(ev.target.result);
    reader.readAsDataURL(file);
  };

  const sendChatMessage = async () => {
    const message = chatInput.trim();
    if ((!message && !chatImage) || chatLoading) return;
    if (!extractionOptions.drawingFormatId) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Please select a format first.' }]);
      return;
    }
    const userMsg = { role: 'user', content: message || '(image attached)', image: chatImage || null };
    setChatMessages(prev => [...prev, userMsg]);
    setChatInput('');
    const sentImage = chatImage;
    setChatImage(null);
    setChatLoading(true);
    try {
      const chatEndpoint = extractorMode === 'v2'
        ? '/pdf-extractor/v2/format-chat'
        : '/pdf-extractor/format-chat';
      const response = await authenticatedApiCall(instance, accounts, 'POST', chatEndpoint, {
        formatId: extractionOptions.drawingFormatId,
        message: message || 'Please analyze this screenshot',
        conversationHistory: chatMessages.slice(-10),
        lastExtractionResults,
        imageBase64: sentImage || null,
      });
      if (response.data.success) {
        const { reply, config_changes, should_rerun } = response.data.data;
        let content = reply;
        if (config_changes && Object.keys(config_changes).length > 0) {
          const changedKeys = Object.keys(config_changes).filter(k => {
            const v = config_changes[k];
            return v && (Array.isArray(v) ? v.length > 0 : typeof v === 'object' ? Object.keys(v).length > 0 : true);
          });
          if (changedKeys.length > 0) {
            content += `\n\n_Applied changes to: ${changedKeys.join(', ')}._`;
          }
        }
        if (should_rerun) content += '\n\n_Re-run extraction to see updated results._';
        setChatMessages(prev => [...prev, { role: 'assistant', content }]);
      } else {
        setChatMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, error occurred. Try again.' }]);
      }
    } catch (err) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: `Error: ${err.response?.data?.details || err.message}` }]);
    } finally { setChatLoading(false); }
  };

  const handleChatKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMessage(); }
  };

  // ── Render ────────────────────────────────────────────

  const renderProgress = () => {
    if (!jobStatus) return null;
    const progress = jobStatus.progress || 0;
    const status = jobStatus.status;
    const isV2 = currentJob?.mode === 'v2';
    let bgColor = '#0d6efd';
    if (status === 'success') bgColor = '#198754';
    if (status === 'error' || status === 'failed') bgColor = '#dc3545';

    const pages = isV2 && jobStatus.total_pages
      ? `${jobStatus.processed_pages || 0}/${jobStatus.total_pages} pages`
      : null;

    const elapsed = currentJob?.startedAt
      ? Math.round((Date.now() - new Date(currentJob.startedAt).getTime()) / 1000)
      : 0;
    const eta = progress > 5 && progress < 100
      ? Math.round((elapsed / progress) * (100 - progress))
      : null;

    return (
      <div style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 14 }}>
          <span>
            Status: <strong>{status}</strong>
            {isV2 && <span style={{ color: '#0d6efd', marginLeft: 8, fontSize: 11, fontWeight: 500 }}>V2</span>}
          </span>
          <span>
            {progress}%
            {pages && <span style={{ color: '#6c757d', marginLeft: 8 }}>({pages})</span>}
            {eta && status !== 'success' && status !== 'failed' && <span style={{ color: '#6c757d', marginLeft: 8 }}>~{eta}s left</span>}
          </span>
        </div>
        <div style={{ width: '100%', height: 20, backgroundColor: '#e9ecef', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ width: `${progress}%`, height: '100%', backgroundColor: bgColor, transition: 'width 0.3s' }} />
        </div>
        {(status === 'error' || status === 'failed') && (jobStatus.error || jobStatus.error_message) && (
          <div style={{ color: '#dc3545', marginTop: 8 }}>Error: {jobStatus.error || jobStatus.error_message}</div>
        )}
        {stallWarning && status !== 'success' && status !== 'failed' && status !== 'error' && (
          <div style={{ marginTop: 8, padding: '10px 14px', backgroundColor: '#fff3cd', border: '1px solid #ffecb5', borderRadius: 6, color: '#664d03', fontSize: 13 }}>
            <strong>Warning:</strong> Progress has not changed for over 5 minutes. The extraction may be stuck.
            Try clicking "Cancel" and re-submitting, or contact support if the issue persists.
          </div>
        )}
        {isV2 && status === 'success' && jobStatus.stats && (
          <div style={{ marginTop: 8, padding: '8px 12px', backgroundColor: '#d1e7dd', borderRadius: 6, fontSize: 13 }}>
            Completed in {jobStatus.stats.totalTimeSeconds}s | {jobStatus.stats.totalPages} pages | {jobStatus.stats.concurrency}x parallel
          </div>
        )}
      </div>
    );
  };

  const selectedFormatObj = extractorMode === 'v2'
    ? v2Formats.find(f => f.id === extractionOptions.drawingFormatId)
    : availableFormats.find(f => f.slug === extractionOptions.format);

  const activeV2Formats = v2Formats.filter(f => f.is_active !== false && !f.is_archived);
  const v2FormatsByFirm = groupFormatsByFirm(activeV2Formats);

  const hasBuilderAccess = formatBuilderAccess || regionEditorAccess || v2BuilderAccess;
  // Same chat size on both modes (slightly taller than the original 420).
  const chatHeight = 460;

  const renderV2FormatOptions = () => (
    v2FormatsByFirm.map(({ firm, formats }) => (
      <optgroup key={firm} label={firm}>
        {formats.map(f => (
          <option key={f.id} value={f.id}>{f.name}</option>
        ))}
      </optgroup>
    ))
  );

  return (
    <div style={{ padding: '16px 20px', maxWidth: '100%', margin: 0, boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, margin: 0 }}>
          {formatBuilderOpen ? 'Format Builder' : 'BOM Extractor'}
        </h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
          <button onClick={() => setHelpOpen(true)} style={{ padding: '8px 14px', fontSize: 13, border: '1px solid #dee2e6', borderRadius: 6, backgroundColor: '#fff', color: '#495057', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 16, fontWeight: 700 }}>?</span> Help
          </button>
          {hasBuilderAccess && (
            <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #ced4da' }} role="group" aria-label="Page mode">
              <button
                type="button"
                onClick={() => setFormatBuilderOpen(false)}
                style={{
                  padding: '8px 14px', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer',
                  backgroundColor: !formatBuilderOpen ? '#212529' : '#fff',
                  color: !formatBuilderOpen ? '#fff' : '#495057',
                }}
              >
                BOM Extractor
              </button>
              <button
                type="button"
                onClick={() => setFormatBuilderOpen(true)}
                style={{
                  padding: '8px 14px', fontSize: 13, fontWeight: 600, border: 'none', borderLeft: '1px solid #ced4da', cursor: 'pointer',
                  backgroundColor: formatBuilderOpen ? '#212529' : '#fff',
                  color: formatBuilderOpen ? '#fff' : '#495057',
                }}
              >
                Format Builder
              </button>
            </div>
          )}
        </div>
      </div>
      <p style={{ color: '#6c757d', marginBottom: 20, fontSize: 14 }}>
        {formatBuilderOpen
          ? 'Manage drawing formats by engineering firm. Create/edit custom formats, attach sample drawings, or archive ones you do not need. Switch back to BOM Extractor anytime with the toggle above.'
          : <>Extract Bill of Materials data from PDF documents.{jobNumber && <span> (Project: {jobNumber})</span>}</>}
      </p>

      {/* Main row: extract (or Format Builder) left | Format Tuning chat right */}
      <div style={{ display: 'flex', gap: 20, alignItems: 'stretch' }}>
        {/* Left column */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {formatBuilderOpen ? (
            <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden', backgroundColor: '#fff', minHeight: chatHeight }}>
              <FormatBuilderPanel
                instance={instance}
                accounts={accounts}
                onFormatsChanged={fetchFormats}
                routePrefix={extractorMode === 'v2' && (regionEditorAccess || v2BuilderAccess) ? '/pdf-extractor/v2/builder' : '/pdf-extractor/builder'}
              />
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderRadius: 8, overflow: 'hidden', border: '1px solid #dee2e6' }}>
                <button onClick={() => setExtractorMode('v2')} style={{
                  flex: 1, padding: '10px 16px', fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer',
                  backgroundColor: extractorMode === 'v2' ? '#0d6efd' : '#f8f9fa',
                  color: extractorMode === 'v2' ? '#fff' : '#495057',
                }}>
                  V2 Extractor (Fast)
                </button>
                <button onClick={() => setExtractorMode('legacy')} style={{
                  flex: 1, padding: '10px 16px', fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer',
                  borderLeft: '1px solid #dee2e6',
                  backgroundColor: extractorMode === 'legacy' ? '#6c757d' : '#f8f9fa',
                  color: extractorMode === 'legacy' ? '#fff' : '#495057',
                }}>
                  Legacy Extractor
                </button>
              </div>
              {extractorMode === 'v2' && (
                <div style={{ padding: '8px 12px', backgroundColor: '#e7f1ff', borderRadius: 6, fontSize: 13, marginBottom: 16, color: '#0a58ca' }}>
                  V2 uses parallel processing (5 pages at once) for ~5x faster extraction. Results are equivalent.
                </div>
              )}

              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, backgroundColor: serviceHealth === 'online' ? '#d1e7dd' : (extractorMode === 'v2' ? '#d1e7dd' : '#f8d7da'), borderRadius: restartMessage ? '6px 6px 0 0' : 6 }}>
                  <span style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: serviceHealth === 'online' ? '#198754' : '#dc3545' }} />
                  <span>PDF Extractor: {serviceHealth === 'online' ? 'Online' : 'Offline'}</span>
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                    <button onClick={handleRestartService} disabled={restarting} style={{
                      padding: '4px 12px', fontSize: 12, borderRadius: 4, fontWeight: 500,
                      border: serviceHealth === 'offline' ? '1px solid #dc3545' : '1px solid #6c757d',
                      backgroundColor: restarting ? '#e9ecef' : serviceHealth === 'offline' ? '#dc3545' : 'transparent',
                      color: restarting ? '#6c757d' : serviceHealth === 'offline' ? '#fff' : '#495057',
                      cursor: restarting ? 'not-allowed' : 'pointer',
                    }}>
                      {restarting ? 'Restarting...' : 'Restart Service'}
                    </button>
                    <button onClick={checkHealth} style={{ padding: '4px 12px', fontSize: 12, border: '1px solid #6c757d', borderRadius: 4, backgroundColor: 'transparent', cursor: 'pointer' }}>Refresh</button>
                  </div>
                </div>
                {restartMessage && (
                  <div style={{ padding: '8px 12px', fontSize: 13, borderRadius: '0 0 6px 6px', backgroundColor: restartMessage.type === 'success' ? '#d1e7dd' : '#f8d7da', color: restartMessage.type === 'success' ? '#0f5132' : '#842029', borderTop: '1px solid ' + (restartMessage.type === 'success' ? '#badbcc' : '#f5c2c7') }}>
                    {restartMessage.text}
                  </div>
                )}
              </div>

              {error && <div style={{ padding: '12px 16px', backgroundColor: '#f8d7da', border: '1px solid #f5c2c7', borderRadius: 6, color: '#842029', marginBottom: 16 }}>{error}</div>}

              {!currentJob && (
                <div style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 8, padding: 24, marginBottom: 24 }}>
                  <h2 style={{ fontSize: 18, fontWeight: 500, marginBottom: 16 }}>Upload PDF</h2>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Select PDF File</label>
                    <input ref={fileInputRef} type="file" accept=".pdf" onChange={handleFileSelect} style={{ width: '100%', padding: 8, border: '1px solid #ced4da', borderRadius: 4 }} />
                    {selectedFile && <div style={{ marginTop: 8, color: '#198754' }}>Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)</div>}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 16 }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>Unit System</label>
                      <select value={extractionOptions.unitSystem} onChange={(e) => handleOptionChange('unitSystem', e.target.value)} style={selectStyle}>
                        <option value="Imperial">Imperial</option><option value="Metric">Metric</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>Format</label>
                      {formatsLoading ? <select disabled style={selectStyle}><option>Loading...</option></select>
                        : formatsError ? <select disabled style={{ ...selectStyle, borderColor: '#dc3545' }}><option>Failed</option></select>
                        : extractorMode === 'v2'
                        ? <select value={extractionOptions.drawingFormatId || ''} onChange={(e) => {
                            const sel = activeV2Formats.find(f => f.id === parseInt(e.target.value, 10));
                            if (sel) setExtractionOptions(prev => ({ ...prev, format: sel.slug, drawingFormatId: sel.id }));
                          }} style={selectStyle}>
                            {renderV2FormatOptions()}
                          </select>
                        : <select value={extractionOptions.format} onChange={(e) => handleOptionChange('format', e.target.value)} style={selectStyle}>
                            {availableFormats.filter(f => f.is_active !== false).map(f => <option key={f.id} value={f.slug}>{f.name}</option>)}
                          </select>}
                      {selectedFormatObj?.description && <div style={{ fontSize: 11, color: '#6c757d', marginTop: 4, lineHeight: 1.4 }}>{selectedFormatObj.description}</div>}
                      {regionEditorAccess && extractorMode === 'v2' && extractionOptions.drawingFormatId && (
                        <button onClick={async () => {
                          try {
                            const res = await authenticatedApiCall(instance, accounts, 'GET', `/pdf-extractor/v2/formats/${extractionOptions.drawingFormatId}/config`);
                            if (res.data.success && res.data.data.regions) {
                              setRegionEditorRegions(normalizeRegionsForEditor(res.data.data.regions));
                            }
                          } catch { /* use empty */ }
                          setRegionEditorOpen(true);
                        }} style={{ marginTop: 6, padding: '4px 10px', fontSize: 12, backgroundColor: '#f8f9fa', border: '1px solid #ced4da', borderRadius: 4, cursor: 'pointer', color: '#495057' }}>
                          Edit Regions
                        </button>
                      )}
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>File Type</label>
                      <select value={extractionOptions.fileType} onChange={(e) => handleOptionChange('fileType', e.target.value)} style={selectStyle}>
                        <option value="Isometrics">Isometrics</option><option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                  {extractorMode === 'v2' && (
                    <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" id="notifyEmail" checked={notifyEmail} onChange={(e) => setNotifyEmail(e.target.checked)} style={{ width: 16, height: 16 }} />
                      <label htmlFor="notifyEmail" style={{ fontSize: 14, color: '#495057', cursor: 'pointer' }}>
                        Email me when extraction completes
                      </label>
                    </div>
                  )}
                  {(() => {
                    const canExtract = extractorMode === 'v2'
                      ? selectedFile && !isUploading && extractionOptions.drawingFormatId
                      : selectedFile && !isUploading && serviceHealth === 'online' && extractionOptions.format;
                    return (
                      <button onClick={handleExtract} disabled={!canExtract} style={{
                        backgroundColor: !canExtract ? '#e9ecef' : extractorMode === 'v2' ? '#0d6efd' : '#f8f9fa',
                        color: !canExtract ? '#adb5bd' : extractorMode === 'v2' ? '#fff' : '#212529',
                        border: '1px solid #dee2e6', borderRadius: 6, padding: '12px 24px', fontSize: 16, fontWeight: 500, width: '100%',
                        cursor: !canExtract ? 'not-allowed' : 'pointer',
                      }}>
                        {isUploading ? 'Uploading...' : extractorMode === 'v2' ? 'Extract BOM Data (V2)' : 'Extract BOM Data'}
                      </button>
                    );
                  })()}
                </div>
              )}

              {currentJob && (
                <div style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 8, padding: 24, marginBottom: 24 }}>
                  <h2 style={{ fontSize: 18, fontWeight: 500, marginBottom: 16 }}>Extraction Progress</h2>
                  <p><strong>File:</strong> {currentJob.filename}</p>
                  <p><strong>Job ID:</strong> {currentJob.id}</p>
                  <p><strong>Format:</strong> {selectedFormatObj?.name || extractionOptions.format}</p>
                  {renderProgress()}
                  <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                    {jobStatus?.status === 'success' && <button onClick={handleDownload} disabled={downloading} style={{ backgroundColor: downloading ? '#5cb85c' : '#198754', color: '#fff', border: 'none', borderRadius: 6, padding: '12px 24px', fontSize: 16, fontWeight: 500, cursor: downloading ? 'wait' : 'pointer', flex: 1 }}>{downloading ? 'Downloading...' : 'Download XLSX'}</button>}
                    <button onClick={handleReset} style={{ backgroundColor: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, padding: '12px 24px', fontSize: 16, fontWeight: 500, cursor: 'pointer', flex: jobStatus?.status === 'success' ? 0 : 1 }}>
                      {jobStatus?.status === 'success' ? 'New Extraction' : 'Cancel'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Right column — Format Tuning chat (AI assistant only — not for PDF extract) */}
        <div style={{
          width: 400, flexShrink: 0, position: 'sticky', top: 16,
          backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e0e0e0',
          display: 'flex', flexDirection: 'column', height: chatHeight, overflow: 'hidden',
        }}>
          <div style={{ padding: '10px 14px', backgroundColor: extractorMode === 'v2' ? '#e7f1ff' : '#f8f9fa', color: '#495057', fontWeight: 600, fontSize: 14, borderRadius: '12px 12px 0 0', borderBottom: '1px solid #e9ecef', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Format Tuning Chat</span>
            <span style={{ fontSize: 11, fontWeight: 500, color: extractorMode === 'v2' ? '#0a58ca' : '#6c757d' }}>
              {extractorMode === 'v2' ? 'V2' : 'Legacy'}
            </span>
          </div>
          {formatBuilderOpen && (
            <div style={{ padding: '8px 12px', borderBottom: '1px solid #e9ecef', backgroundColor: '#fff' }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6c757d', marginBottom: 4 }}>
                Which format should the chat tune?
              </label>
              <select
                value={extractionOptions.drawingFormatId || ''}
                onChange={(e) => {
                  const sel = activeV2Formats.find(f => f.id === parseInt(e.target.value, 10));
                  if (sel) setExtractionOptions(prev => ({ ...prev, format: sel.slug, drawingFormatId: sel.id }));
                }}
                style={{ ...selectStyle, fontSize: 12, padding: '6px 8px' }}
              >
                {renderV2FormatOptions()}
              </select>
              <div style={{ fontSize: 11, color: '#6c757d', marginTop: 4, lineHeight: 1.35 }}>
                This dropdown is only for the AI chat — not for extracting a PDF. To extract, switch to <strong>BOM Extractor</strong>.
              </div>
            </div>
          )}
          <div style={{ flex: 1, overflowY: 'auto', padding: 12, backgroundColor: '#fafafa', minHeight: 0 }}>
            {chatMessages.length === 0 && (
              <div style={{ backgroundColor: '#fff', border: '1px solid #e9ecef', borderRadius: 8, padding: 12, fontSize: 13 }}>
                <p style={{ fontWeight: 500, marginBottom: 6 }}>AI Format Assistant {extractorMode === 'v2' && <span style={{ color: '#0a58ca', fontSize: 11 }}>(V2)</span>}</p>
                <p style={{ color: '#6c757d', marginBottom: 6 }}>
                  {extractorMode === 'v2'
                    ? 'Ask me to adjust regions, prompts, or model settings for the format selected above.'
                    : 'Describe issues and I\'ll adjust the config.'}
                </p>
                <ul style={{ margin: 0, paddingLeft: 16, color: '#495057', fontSize: 12 }}>
                  <li>"Table is cut off on the right"</li>
                  <li>"Confusing quantity with size"</li>
                  <li>"Widen table region 10%"</li>
                  {extractorMode === 'v2' && <li>"Add a note about bolt sets to the prompt"</li>}
                </ul>
                <p style={{ color: '#6c757d', marginTop: 6, fontSize: 12 }}>Tip: paste a screenshot with Ctrl+V</p>
              </div>
            )}
            {chatMessages.map((msg, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 6 }}>
                <div style={{
                  maxWidth: '85%', padding: '8px 12px',
                  borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                  backgroundColor: msg.role === 'user' ? '#e9ecef' : '#f0f0f0',
                  color: '#212529', fontSize: 13, lineHeight: 1.4, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}>
                  {msg.image && <img src={msg.image} alt="attached" style={{ maxWidth: '100%', borderRadius: 6, marginBottom: 4 }} />}
                  {msg.content}
                </div>
              </div>
            ))}
            {chatLoading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 6 }}>
                <div style={{ padding: '8px 12px', borderRadius: '14px 14px 14px 4px', backgroundColor: '#f0f0f0', fontSize: 13, color: '#6c757d' }}>Thinking...</div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
          {chatImage && (
            <div style={{ padding: '4px 12px', backgroundColor: '#e7f1ff', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
              <img src={chatImage} alt="preview" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4 }} />
              <span style={{ flex: 1, color: '#495057' }}>Image attached</span>
              <button onClick={() => setChatImage(null)} style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: 16 }}>&times;</button>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, padding: '8px 10px', borderTop: '1px solid #e9ecef', backgroundColor: '#fff', borderRadius: '0 0 12px 12px' }}>
            <input ref={chatImageInputRef} type="file" accept="image/*" onChange={handleImageAttach} style={{ display: 'none' }} />
            <button onClick={() => chatImageInputRef.current?.click()} title="Attach image" style={{ padding: '6px 8px', background: 'none', border: '1px solid #ced4da', borderRadius: 6, cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>
              📎
            </button>
            <textarea
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={handleChatKeyDown}
              onPaste={handleImagePaste}
              placeholder="Describe an issue..."
              rows={4}
              style={{ flex: 1, padding: '8px 10px', border: '1px solid #ced4da', borderRadius: 8, fontSize: 13, resize: 'vertical', fontFamily: 'inherit', outline: 'none', minHeight: 80, maxHeight: 140 }}
            />
            <button onClick={sendChatMessage} disabled={(!chatInput.trim() && !chatImage) || chatLoading} style={{
              padding: '8px 12px', backgroundColor: '#f8f9fa', color: '#495057', border: '1px solid #dee2e6', borderRadius: 8, fontSize: 13, fontWeight: 500,
              opacity: (!chatInput.trim() && !chatImage) || chatLoading ? 0.5 : 1,
              cursor: (!chatInput.trim() && !chatImage) || chatLoading ? 'not-allowed' : 'pointer',
            }}>Send</button>
          </div>
        </div>
      </div>

      {/* Help Modal */}
      {helpOpen && <HelpModal onClose={() => setHelpOpen(false)} hasBuilderAccess={formatBuilderAccess || v2BuilderAccess} />}

      {/* Region Editor Modal */}
      {regionEditorOpen && ReactDOM.createPortal(
        <div onClick={() => setRegionEditorOpen(false)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: '#fff', borderRadius: 12, width: '95vw', maxWidth: 1200, maxHeight: '90vh', overflow: 'auto', padding: 24, position: 'relative' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>
                Edit Regions — {v2Formats.find(f => f.id === extractionOptions.drawingFormatId)?.name || 'Format'}
              </h2>
              <button onClick={() => setRegionEditorOpen(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#6c757d' }}>&times;</button>
            </div>
            <p style={{ color: '#6c757d', fontSize: 13, marginBottom: 16 }}>
              Upload a sample PDF, then draw rectangles to define where the BOM table and metadata (title block) are on the page.
              Drag to move, use the corner handle to resize. Changes are saved as overrides and used on next extraction.
            </p>
            <RegionEditor
              formatId={extractionOptions.drawingFormatId}
              regions={regionEditorRegions}
              instance={instance}
              accounts={accounts}
              pdfToImageEndpoint="/pdf-extractor/v2/pdf-to-image"
              onSave={async (regions) => {
                try {
                  const payload = {
                    formatId: extractionOptions.drawingFormatId,
                    regions: regions.map((r, i) => ({
                      id: r.id || (i + 1) * 100,
                      name: r.region_name,
                      label: r.region_label,
                      type: r.type || 'table',
                      left: r.left_percent,
                      top: r.top_percent,
                      width: r.width_percent,
                      height: r.height_percent,
                    })),
                  };
                  const res = await authenticatedApiCall(instance, accounts, 'POST', '/pdf-extractor/v2/format-overrides', payload);
                  if (res.data.success) {
                    alert('Regions saved. Re-run extraction to use the new regions.');
                    return regions;
                  }
                } catch (err) {
                  alert('Failed to save regions: ' + (err.response?.data?.error || err.message));
                }
                return null;
              }}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// ── Help Modal ──────────────────────────────────────────

const HELP_TABS = [
  { id: 'guide', label: 'User Guide' },
  { id: 'process', label: 'Process Flow' },
  ...(hasHelpTour('bom-extractor-help') ? [{ id: 'tour', label: 'Video Tour' }] : []),
];

const HelpModal = ({ onClose, hasBuilderAccess }) => {
  const [activeTab, setActiveTab] = useState('guide');

  return (
  <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
    <div style={{ backgroundColor: '#fff', borderRadius: 12, width: 820, maxHeight: '85vh', overflow: 'auto', padding: 32, position: 'relative' }} onClick={e => e.stopPropagation()}>
      <div style={{ position: 'absolute', top: 12, right: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
        <ListenButton compact guideId="bom-extractor-help" title="BOM Extractor Help" accent="#0d6efd" />
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#6c757d' }}>&times;</button>
      </div>

      <h2 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4, color: '#0d6efd' }}>BOM Extractor — Help Guide</h2>
      <p style={{ color: '#6c757d', marginBottom: 16 }}>Complete guide to extracting Bill of Materials from PDF drawings</p>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid #e9ecef', marginBottom: 24 }}>
        {HELP_TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            style={{
              padding: '10px 18px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: 14, fontWeight: 600,
              color: activeTab === t.id ? '#0369a1' : '#6c757d',
              borderBottom: activeTab === t.id ? '2px solid #0369a1' : '2px solid transparent',
              marginBottom: -2,
            }}
          >{t.label}</button>
        ))}
      </div>

      {activeTab === 'process' && <ProcessFlow />}

      {activeTab === 'tour' && (
        <HelpVideoTour guideId="bom-extractor-help" title="BOM Extractor Help" accent="#0d6efd" compact showHeader />
      )}

      {activeTab === 'guide' && (<>
      {/* Section 1: How to Extract */}
      <div style={sectionStyle}>
        <div style={{ ...sectionHeaderStyle, backgroundColor: '#0d6efd' }}>
          <span style={{ fontSize: 20 }}>📄</span>
          <span>How to Extract BOM Data</span>
        </div>
        <div style={sectionBodyStyle}>
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            <li style={liStyle}><strong>Upload a PDF</strong> — Click "Choose File" and select a PDF isometric drawing (max 50MB)</li>
            <li style={liStyle}><strong>Select options</strong> — Choose unit system (Imperial/Metric), drawing format, and file type</li>
            <li style={liStyle}><strong>Click "Extract BOM Data"</strong> — The AI will process your PDF and extract the bill of materials</li>
            <li style={liStyle}><strong>Download results</strong> — When complete, download the structured XLSX file</li>
          </ol>
        </div>
      </div>

      {/* Section 2: Tuning Assistant */}
      <div style={sectionStyle}>
        <div style={{ ...sectionHeaderStyle, backgroundColor: '#198754' }}>
          <span style={{ fontSize: 20 }}>🤖</span>
          <span>Format Tuning Assistant</span>
        </div>
        <div style={sectionBodyStyle}>
          <p style={{ marginBottom: 8 }}>The AI assistant on the right helps you fine-tune extraction results without editing code.</p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={helpThStyle}>You say...</th>
              <th style={helpThStyle}>AI does...</th>
            </tr></thead>
            <tbody>
              <tr><td style={helpTdStyle}>"The BOM table is cut off on the right"</td><td style={helpTdStyle}>Widens the table region</td></tr>
              <tr><td style={helpTdStyle}>"It's confusing quantity with size"</td><td style={helpTdStyle}>Adjusts the AI prompt</td></tr>
              <tr><td style={helpTdStyle}>"Widen table region by 10%"</td><td style={helpTdStyle}>Expands region coordinates</td></tr>
              <tr><td style={helpTdStyle}>"Title block isn't captured"</td><td style={helpTdStyle}>Adjusts metadata region</td></tr>
            </tbody>
          </table>
          <div style={{ marginTop: 12, padding: '8px 12px', backgroundColor: '#e7f1ff', borderRadius: 6, fontSize: 13 }}>
            <strong>Tip:</strong> Paste a screenshot (Ctrl+V) or click the 📎 button to attach an image showing the AI exactly what's wrong.
          </div>
        </div>
      </div>

      {/* Section 3: Drawing Formats */}
      <div style={sectionStyle}>
        <div style={{ ...sectionHeaderStyle, backgroundColor: '#6f42c1' }}>
          <span style={{ fontSize: 20 }}>📐</span>
          <span>Drawing Formats</span>
        </div>
        <div style={sectionBodyStyle}>
          <p style={{ marginBottom: 8 }}>Each format defines how to extract data from a specific type of engineering drawing:</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13 }}>
            <div style={helpCardStyle}><strong>Regions</strong> — Rectangle areas on the PDF (BOM table, title block)</div>
            <div style={helpCardStyle}><strong>Columns</strong> — Fields to extract (qty, size, description)</div>
            <div style={helpCardStyle}><strong>AI Prompts</strong> — Instructions for GPT-4o per region</div>
            <div style={helpCardStyle}><strong>Excel Mapping</strong> — How extracted data maps to spreadsheet columns</div>
          </div>
        </div>
      </div>

      {/* Section 4: Permissions & Access Control */}
      <div style={sectionStyle}>
        <div style={{ ...sectionHeaderStyle, backgroundColor: '#fd7e14' }}>
          <span style={{ fontSize: 20 }}>🔐</span>
          <span>Permissions & Access Control</span>
        </div>
        <div style={sectionBodyStyle}>
          <p style={{ fontSize: 13, color: '#495057', marginBottom: 12 }}>
            This tool has a layered permission system. Here is exactly who can do what:
          </p>

          {/* Page Access */}
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 6, color: '#212529' }}>Page Access</h4>
            <div style={{ padding: '10px 14px', backgroundColor: '#f8f9fa', borderRadius: 6, border: '1px solid #e9ecef', fontSize: 13 }}>
              <p style={{ margin: '0 0 6px' }}>Only these 4 users can access the BOM Extractor page:</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, paddingLeft: 8 }}>
                <span><strong>Jordan Eamon</strong> — jeamon@</span>
                <span><strong>Yousef Rezaei</strong> — yrezaei@</span>
                <span><strong>TJ Harquail</strong> — tharquail@</span>
                <span><strong>Jose Amarante</strong> — jamarante@</span>
              </div>
              <p style={{ margin: '6px 0 0', color: '#6c757d' }}>All other users will see a "403 Access Denied" if they try to access this page.</p>
            </div>
          </div>

          {/* Extraction Permissions */}
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 6, color: '#212529' }}>Extraction</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={helpThStyle}>Action</th>
                <th style={helpThStyle}>Who can do it</th>
              </tr></thead>
              <tbody>
                <tr><td style={helpTdStyle}>Upload PDF & extract BOM</td><td style={helpTdStyle}>All 4 authorized users</td></tr>
                <tr><td style={helpTdStyle}>Download XLSX results</td><td style={helpTdStyle}>All 4 authorized users</td></tr>
                <tr><td style={helpTdStyle}>Use Format Tuning Assistant</td><td style={helpTdStyle}>All 4 authorized users</td></tr>
                <tr><td style={helpTdStyle}>Attach/paste images in chat</td><td style={helpTdStyle}>All 4 authorized users</td></tr>
              </tbody>
            </table>
          </div>

          {/* Format Builder Permissions */}
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 6, color: '#212529' }}>Format Builder</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={helpThStyle}>Action</th>
                <th style={helpThStyle}>Who can do it</th>
              </tr></thead>
              <tbody>
                <tr><td style={helpTdStyle}>View Format Builder</td><td style={helpTdStyle}>All 4 authorized users</td></tr>
                <tr><td style={helpTdStyle}>Create a new format</td><td style={helpTdStyle}>All 4 authorized users (creator recorded)</td></tr>
                <tr><td style={helpTdStyle}>Duplicate a format</td><td style={helpTdStyle}>All 4 authorized users (new copy belongs to you)</td></tr>
                <tr><td style={helpTdStyle}>Edit / Delete a format</td><td style={helpTdStyle}>See ownership rules below</td></tr>
              </tbody>
            </table>
          </div>

          {/* Ownership Rules */}
          <div>
            <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 6, color: '#212529' }}>Format Ownership Rules</h4>
            <div style={{ border: '1px solid #e9ecef', borderRadius: 8, overflow: 'hidden', fontSize: 13 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', borderBottom: '2px solid #dee2e6' }}>
                <div style={{ padding: '8px 12px', fontWeight: 600, backgroundColor: '#f8f9fa' }}>Scenario</div>
                <div style={{ padding: '8px 12px', fontWeight: 600, backgroundColor: '#f8f9fa' }}>Jordan, Yousef, TJ</div>
                <div style={{ padding: '8px 12px', fontWeight: 600, backgroundColor: '#f8f9fa' }}>Jose</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', borderBottom: '1px solid #f0f0f0' }}>
                <div style={{ padding: '8px 12px' }}>Format created by Jose</div>
                <div style={{ padding: '8px 12px', color: '#198754' }}>Full access (edit, delete, toggle)</div>
                <div style={{ padding: '8px 12px', color: '#198754' }}>Full access (owner)</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', borderBottom: '1px solid #f0f0f0' }}>
                <div style={{ padding: '8px 12px' }}>Format created by Jordan, Yousef, or TJ</div>
                <div style={{ padding: '8px 12px', color: '#198754' }}>Full access</div>
                <div style={{ padding: '8px 12px', color: '#dc3545' }}>Read-only (view only)</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
                <div style={{ padding: '8px 12px' }}>Legacy formats (no owner)</div>
                <div style={{ padding: '8px 12px', color: '#198754' }}>Full access</div>
                <div style={{ padding: '8px 12px', color: '#198754' }}>Full access</div>
              </div>
            </div>
            <div style={{ marginTop: 8, padding: '8px 12px', backgroundColor: '#e7f1ff', borderRadius: 6, fontSize: 12, color: '#495057' }}>
              When you duplicate a format, the copy belongs to you — regardless of who created the original.
            </div>
          </div>
        </div>
      </div>

      {/* Section 5: Format Builder (Admin) — only for builder users */}
      {hasBuilderAccess && (
        <div style={sectionStyle}>
          <div style={{ ...sectionHeaderStyle, backgroundColor: '#dc3545' }}>
            <span style={{ fontSize: 20 }}>🔧</span>
            <span>Format Builder (Admin)</span>
          </div>
          <div style={sectionBodyStyle}>
            <p style={{ marginBottom: 8 }}>Click "Format Builder" in the header to manage formats. You can:</p>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
              <li style={liStyle}><strong>Create</strong> new formats for different drawing types</li>
              <li style={liStyle}><strong>Edit</strong> — Upload a sample PDF and draw regions visually, define columns, write AI prompts, set Excel mappings</li>
              <li style={liStyle}><strong>Duplicate</strong> — Copy an existing format as a starting point</li>
              <li style={liStyle}><strong>Test</strong> — Run extraction with your config and see JSON results instantly</li>
              <li style={liStyle}><strong>Toggle Active</strong> — Enable/disable formats in the dropdown</li>
            </ul>
          </div>
        </div>
      )}
      </>)}
    </div>
  </div>
  );
};

// ── Process Flow diagram (Help > Process Flow tab) ──────────

const FlowBox = ({ icon, title, subtitle, color, sub }) => (
  <div style={{
    border: `2px solid ${color}`, borderRadius: 10, padding: '10px 14px',
    backgroundColor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', textAlign: 'center', minWidth: 150,
  }}>
    <div style={{ fontSize: 20, lineHeight: 1 }}>{icon}</div>
    <div style={{ fontWeight: 700, fontSize: 13, color: '#212529', marginTop: 4 }}>{title}</div>
    {subtitle && <div style={{ fontSize: 11, color: '#6c757d', marginTop: 2 }}>{subtitle}</div>}
    {sub && <div style={{ fontSize: 10, color: color, marginTop: 4, fontWeight: 600 }}>{sub}</div>}
  </div>
);

const DownArrow = ({ label }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '2px 0' }}>
    {label && <span style={{ fontSize: 10, color: '#6c757d', marginBottom: -2 }}>{label}</span>}
    <span style={{ fontSize: 20, color: '#adb5bd', lineHeight: 1 }}>↓</span>
  </div>
);

const ProcessFlow = () => (
  <div style={{ padding: '4px 4px 12px' }}>
    <p style={{ color: '#6c757d', marginBottom: 18, fontSize: 13 }}>
      How estimating data flows end-to-end — from drawings all the way to EVM, with everything feeding Change Management.
    </p>

    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* Inputs */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <FlowBox icon="📑" title="ISO Drawings" subtitle="from bid documents" color="#0d6efd" />
        <span style={{ fontSize: 22, color: '#adb5bd' }}>→</span>
        <FlowBox icon="🧩" title="BOM Extraction" subtitle="this tool" color="#6f42c1" />
      </div>

      <DownArrow />

      {/* Feeders merging into MTO */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, justifyContent: 'center' }}>
        <FlowBox icon="🧩" title="BOM" subtitle="parts list" color="#6f42c1" />
        <FlowBox icon="🗃️" title="Part Rate DB" subtitle="estimated hours" color="#198754" sub="OTT / MCAA rates" />
        <FlowBox icon="💰" title="Purchasing" subtitle="supplier costs" color="#fd7e14" sub="export → supplier → cost back" />
      </div>

      <DownArrow label="feed into" />

      {/* MTO + analytics */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <FlowBox icon="📋" title="MTO List" subtitle="master take-off" color="#0a58ca" />
        <span style={{ fontSize: 22, color: '#adb5bd' }}>→</span>
        <FlowBox icon="📊" title="MTO Analytics" subtitle="summaries & insights" color="#20c997" />
      </div>

      <DownArrow />

      {/* Tracker + analytics */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <FlowBox icon="📈" title="Progress Tracker" subtitle="fab & field progress" color="#0d6efd" />
        <span style={{ fontSize: 22, color: '#adb5bd' }}>→</span>
        <FlowBox icon="📊" title="Tracker Analytics" subtitle="quantity, material, SOV…" color="#20c997" />
      </div>

      <DownArrow />

      <FlowBox icon="🗓️" title="Schedule" subtitle="planned vs actual" color="#6610f2" />

      <DownArrow />

      <FlowBox icon="📉" title="EVM Curves" subtitle="earned value" color="#d63384" />

      {/* Change management receives from all */}
      <div style={{ marginTop: 20, width: '100%' }}>
        <div style={{ borderTop: '2px dashed #ced4da', paddingTop: 14, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: '#6c757d', marginBottom: 8 }}>
            ISO · BOM · MTO · Tracker · Schedule all also feed →
          </span>
          <FlowBox icon="🔁" title="Change Management" subtitle="impact across the whole chain" color="#dc3545" />
        </div>
      </div>
    </div>
  </div>
);

const selectStyle = { width: '100%', padding: 8, border: '1px solid #ced4da', borderRadius: 4 };
const sectionStyle = { marginBottom: 20, border: '1px solid #e9ecef', borderRadius: 8, overflow: 'hidden' };
const sectionHeaderStyle = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', color: '#fff', fontWeight: 600, fontSize: 15 };
const sectionBodyStyle = { padding: 16 };
const liStyle = { marginBottom: 6 };
const helpThStyle = { padding: '8px 12px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #dee2e6' };
const helpTdStyle = { padding: '8px 12px', borderBottom: '1px solid #f0f0f0' };
const helpCardStyle = { padding: '10px 12px', backgroundColor: '#f8f9fa', borderRadius: 6, border: '1px solid #e9ecef' };

export default BomExtractorApi2Page;
